import { ProReviewBoolExp } from 'generated/graphql';
import { useMemo } from 'react';
import {
  BooleanParam,
  NumberParam,
  ObjectParam,
  StringParam,
  useQueryParams,
} from 'use-query-params';

type UseReviewsFiltersParams = {
  currentBilikZoneIds?: number[];
};

const REVIEWS_PER_PAGE = 20;

export const useReviewFilters = ({
  currentBilikZoneIds,
}: UseReviewsFiltersParams) => {
  const [filters, setFilters] = useQueryParams({
    source: StringParam,
    page: NumberParam,
    ip: StringParam,
    id: NumberParam,
    presentationId: NumberParam,
    privateIndividualEmail: StringParam,
    isSpam: BooleanParam,
    status: StringParam,
    orderBy: ObjectParam,
    familyName: StringParam,
    givenName: StringParam,
    hasCheckerId: BooleanParam,
    replyStatus: StringParam,
    allZones: BooleanParam,
    selectedId: NumberParam,
  });

  const whereFiltered: ProReviewBoolExp = useMemo(() => {
    const piCredentialFilters: ProReviewBoolExp[] = [];

    if (filters.givenName || filters.familyName) {
      piCredentialFilters.push({
        familyName: { _eq: filters.familyName ?? undefined },
        givenName: { _eq: filters.givenName ?? undefined },
      });
    }

    if (filters.privateIndividualEmail) {
      piCredentialFilters.push({
        email: { _eq: filters.privateIndividualEmail },
      });
    }

    return {
      id: {
        _eq: filters.id ?? undefined,
      },
      _or: piCredentialFilters.length > 0 ? piCredentialFilters : undefined,
      ip: {
        _eq: filters.ip ?? undefined,
      },
      presentations: filters.presentationId
        ? { presentationId: { _eq: filters.presentationId } }
        : undefined,
    };
  }, [
    filters.privateIndividualEmail,
    filters.familyName,
    filters.givenName,
    filters.ip,
    filters.presentationId,
    filters.id,
  ]);

  const commonFilters: ProReviewBoolExp = useMemo(() => {
    return {
      source: filters.source
        ? filters.source === 'external'
          ? {
              _neq: 'BILIK',
            }
          : {
              _eq: 'BILIK',
            }
        : undefined,
      checkerId: {
        _isNull:
          filters.hasCheckerId === null || filters.hasCheckerId === undefined
            ? undefined
            : !filters.hasCheckerId,
      },
    };
  }, [filters.source, filters.hasCheckerId]);

  const whereTodo: ProReviewBoolExp = useMemo(() => {
    return {
      ...whereFiltered,
      ...commonFilters,
      isSpam: {
        _eq: false,
      },
      _and: [
        {
          _or: [
            { status: { _eq: 'draft' } },
            {
              _and: [
                { replyStatus: { _eq: 'draft' } },
                { status: { _neq: 'ko' } },
              ],
            },
          ],
        },
        {
          referenceView: {
            view: {
              status: { _in: ['published', 'draft'] },
              ...(!filters.allZones && currentBilikZoneIds
                ? { bilikZoneId: { _in: currentBilikZoneIds } }
                : {}),
            },
          },
        },
      ],
    };
  }, [whereFiltered, commonFilters, filters.allZones, currentBilikZoneIds]);

  const whereTable: ProReviewBoolExp = useMemo(() => {
    if (filters.status === 'todo') {
      return whereTodo;
    }

    return {
      ...whereFiltered,
      ...commonFilters,
      isSpam: {
        _eq: filters.isSpam ?? undefined,
      },
      replyStatus: {
        _eq: filters.replyStatus ?? undefined,
      },
      status: {
        _eq: filters.status ?? undefined,
      },
      _and: [
        {
          presentations: {
            presentation: {
              proViews:
                !filters.allZones && currentBilikZoneIds
                  ? {
                      bilikZoneId: { _in: currentBilikZoneIds },
                      status: { _neq: 'archived' },
                    }
                  : {},
            },
          },
        },
      ],
    };
  }, [
    filters.isSpam,
    filters.status,
    filters.replyStatus,
    filters.allZones,
    currentBilikZoneIds,
    whereFiltered,
    whereTodo,
    commonFilters,
  ]);

  return {
    filters,
    setFilters,
    reviewsPerPage: REVIEWS_PER_PAGE,
    whereTable,
    whereTodo,
  };
};

export type ReviewsFilters = ReturnType<typeof useReviewFilters>['filters'];
